package diana.soleil.design.iraniancanadienbazzar.feature.admin

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.DealsRepository
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppBottomSheet
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppChip
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIconButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppTextField
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.model.Business
import diana.soleil.design.iraniancanadienbazzar.core.model.LocalizedText
import diana.soleil.design.iraniancanadienbazzar.core.model.formatCount
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_offer_days_chip
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_offer_delete_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_offer_ended
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_offer_ends_days
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_offer_failed
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_offer_headline_en
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_offer_headline_fa
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_offer_intro
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_offer_publish
import diana.soleil.design.iraniancanadienbazzar.feature.admin.resources.admin_offer_title
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import kotlin.time.Clock

/**
 * Admin "post an offer on a merchant's behalf" composer (2026-09-11). Claims are still rare, so
 * offers gathered by phone would otherwise have no way into the Hot Deals block; Firestore rules
 * let admins create `deals` with the same shape an owner writes. Lists the business's existing
 * offers (delete = moderation) and publishes a new one: EN headline, optional FA, 7/14/30 days.
 */
@Composable
internal fun AdminOfferSheet(
    business: Business,
    onDismiss: () -> Unit,
) {
    val repo = koinInject<DealsRepository>()
    val existingResult by remember(business.id) { repo.observeDealsForBusiness(business.id) }
        .collectAsState(initial = DataResult.Success(emptyList()))
    val existing = (existingResult as? DataResult.Success)?.data.orEmpty()
    var headlineEn by remember(business.id) { mutableStateOf("") }
    var headlineFa by remember(business.id) { mutableStateOf("") }
    var days by remember(business.id) { mutableStateOf(14) }
    var saving by remember { mutableStateOf(false) }
    var failed by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    val lang = AppTheme.language
    val nowMillis = Clock.System.now().toEpochMilliseconds()

    AppBottomSheet(onDismissRequest = onDismiss, title = stringResource(Res.string.admin_offer_title, business.name)) {
        Text(
            text = stringResource(Res.string.admin_offer_intro),
            style = AppTheme.typography.bodySm,
            color = AppTheme.colors.onSurfaceVariant,
        )
        existing.forEach { deal ->
            Row(
                modifier = Modifier.fillMaxWidth().padding(vertical = AppTheme.spacing.xs),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
            ) {
                Column(modifier = Modifier.weight(1f)) {
                    Text(text = deal.title.resolve(lang), style = AppTheme.typography.bodyMd, color = AppTheme.colors.onSurface)
                    val daysLeft = ((deal.endsAtMillis - nowMillis) / DAY_MILLIS).toInt().coerceAtLeast(0) + 1
                    Text(
                        text = if (deal.isActive(nowMillis)) stringResource(Res.string.admin_offer_ends_days, lang.formatCount(daysLeft))
                        else stringResource(Res.string.admin_offer_ended),
                        style = AppTheme.typography.labelSm,
                        color = AppTheme.colors.onSurfaceVariant,
                    )
                }
                AppIconButton(
                    iconName = "delete",
                    contentDescription = stringResource(Res.string.admin_offer_delete_a11y),
                    onClick = { scope.launch { repo.deleteDeal(deal.id) } },
                )
            }
        }
        AppTextField(
            value = headlineEn,
            onValueChange = { headlineEn = it },
            label = stringResource(Res.string.admin_offer_headline_en),
            modifier = Modifier.fillMaxWidth().padding(top = AppTheme.spacing.md),
        )
        AppTextField(
            value = headlineFa,
            onValueChange = { headlineFa = it },
            label = stringResource(Res.string.admin_offer_headline_fa),
            modifier = Modifier.fillMaxWidth().padding(top = AppTheme.spacing.sm),
        )
        Row(
            modifier = Modifier.padding(top = AppTheme.spacing.sm),
            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
        ) {
            DAY_OPTIONS.forEach { option ->
                AppChip(
                    label = stringResource(Res.string.admin_offer_days_chip, lang.formatCount(option)),
                    selected = days == option,
                    onClick = { days = option },
                )
            }
        }
        if (failed) {
            Text(
                text = stringResource(Res.string.admin_offer_failed),
                style = AppTheme.typography.bodySm,
                color = AppTheme.colors.error,
                modifier = Modifier.padding(top = AppTheme.spacing.sm),
            )
        }
        AppButton(
            text = stringResource(Res.string.admin_offer_publish),
            onClick = {
                if (headlineEn.isBlank() || saving) return@AppButton
                scope.launch {
                    saving = true
                    failed = false
                    val result = repo.createDeal(
                        businessId = business.id,
                        businessName = business.name,
                        title = LocalizedText(en = headlineEn.trim(), fa = headlineFa.trim().ifBlank { null }),
                        details = null,
                        endsAtMillis = nowMillis + days * DAY_MILLIS,
                    )
                    if (result is DataResult.Success) {
                        headlineEn = ""
                        headlineFa = ""
                    } else {
                        failed = true
                    }
                    saving = false
                }
            },
            enabled = headlineEn.isNotBlank() && !saving,
            loading = saving,
            leadingIcon = "sell",
            modifier = Modifier.fillMaxWidth().padding(top = AppTheme.spacing.lg),
        )
    }
}

private val DAY_OPTIONS = listOf(7, 14, 30)
private const val DAY_MILLIS = 24L * 60 * 60 * 1_000
